import { CONFIG } from "~/config";
import Point from "./point";
import Line from "./line";
import { TYPES } from ".";
import { isMouseLeftOrTouch } from "./util";

export default class Item {
	_owner: any;

	_scene: any;

	_startC: any;

	_endC: any;

	_point: Point;

	_line: Line;

	_prev: Item;

	_next: Item;

	_index: number;

	_wasEnabled: boolean;

	_newPoint: Item;

	_type: number;

	_ownLinePoints: any;

	constructor(owner, scene, startC, endC) {
		this._owner = owner;
		this._scene = scene;
		this._type = TYPES.EMPTY;
		this._startC = startC;
		this._endC = endC;
		this._point = new Point(owner.layers.points, scene);
		this._line = new Line(owner.layers.lines, scene);
		this._ownLinePoints = null; // readonly body
		this._index = -1;
		this._newPoint = null;
		this._prev = null;
		this._next = null;
		this._wasEnabled = false;

		// vytvorime
		this._createPoint();
		this._createLine();
	}

	/**
	 * Odstraneni.
	 */
	destroy() {
		this._point.remove();
		this._line.remove();
	}

	/**
	 * Nastaveni predchoziho bodu - sourozenec.
	 */
	set prev(item) {
		this._prev = item;
	}

	/**
	 * Ziskani predchoziho bodu - sourozenec.
	 */
	get prev() {
		return this._prev;
	}

	/**
	 * Nastaveni dalsiho bodu - sourozenec.
	 */
	set next(item) {
		this._next = item;
	}

	/**
	 * Ziskani dalsiho bodu - sourozenec.
	 */
	get next() {
		return this._next;
	}

	/**
	 * Ziskani instance bodu.
	 */
	get point() {
		return this._point;
	}

	/**
	 * Ziskani instance cary.
	 *
	 * @return {Point}
	 */
	get line() {
		return this._line;
	}

	/**
	 * Nastaveni vlastnich bodu pro caru - pro readonly.
	 * Prekresli caru.
	 *
	 * @param  {Array} linePoints
	 */
	set linePoints(linePoints) {
		if (!Array.isArray(linePoints)) {
			return;
		}

		this._ownLinePoints = linePoints;

		if (this._line.geom) {
			this._line.coords = this._getLinePoints();
		} else {
			this._createLine();
		}
	}

	/**
	 * Nastaveni indexu (poradi).
	 *
	 * @param  {Number} index
	 */
	set index(index) {
		this._index = index;
	}

	/**
	 * Ziskani indexu.
	 *
	 * @return {Number}
	 */
	get index() {
		return this._index;
	}

	/**
	 * Nastaveni typu itemu.
	 *
	 * @param  {Number} type start, cil...
	 */
	set type(type) {
		if (this._type !== type) {
			this._type = type;

			if (this._type === TYPES.START) {
				this._point.setStyleStart();
			} else {
				this._point.clearStyle();
			}
		}
	}

	/**
	 * Nastaveni souradnic - start; pri zmene se prekresli bod, special pro start - musi se vykreslit po care.
	 */
	set startCoords(coords) {
		if (coords && !this._startC.equals(coords)) {
			this._startC = coords;

			this._point.coords = coords;

			if (this._endC) {
				this._line.coords = this._getLinePoints();
			}
		}
	}

	/**
	 * Nastaveni souradnic - konec; pri zmene se prekresli cara a muze i bod; prazdna hodnota smaze caru
	 */
	set endCoords(coords) {
		if (coords && (!this._endC || !this._endC.equals(coords))) {
			const wasEmpty = this._endC === null;

			this._endC = coords;

			// nebyl vytvoren
			if (wasEmpty) {
				this._createLine();
			} else {
				this._line.coords = this._getLinePoints();
				this._line.bindTo(this);
			}
		} else if (!coords) {
			if (this._endC) {
				this._line.remove();
			}

			this._endC = null;
		}
	}

	/**
	 * Bind elementu - pri map redraw se znovu vykresli geometrie, takze stary eventy jsou k nicemu, je nutno je znovu navesit.
	 */
	bindTo() {
		// bind na nas handleEvent
		//this._point.bind(this);
		this._line.bindTo(this);
	}

	/**
	 * Ziskani souradnic - start.
	 */
	getCoords() {
		return this._startC;
	}

	/**
	 * Ziskani souradnic cary.
	 */
	getLinePoints() {
		return this._line.getCoords() || [];
	}

	/**
	 * Nastaveni hoveru celeho itemu.
	 */
	setHover(state) {
		this.setPointHover(state);
		this.setLineHover(state);
	}

	/**
	 * Nastaveni hoveru bodu
	 */
	setPointHover(state) {
		this._point.setHover(this._type === TYPES.START, state);
	}

	/**
	 * Nastaveni hoveru cary
	 */
	setLineHover(state) {
		this._line.setHover(state);
	}

	isTarget(event: PointerEvent) {
		let target = null;

		if (this._point.elTest(event.target)) {
			target = {
				point: true,
				index: this._index,
			};
		// kontext menu - usek
		} else if (this._line.elTest(event.target)) {
			target = {
				point: false,
				index: this._index,
			};
		}

		return target;
	}

	/**
	 * Veskera interakce nad body - tahani, presuny...
	 */
	handleEvent(event: PointerEvent) {
		switch (event.type) {
			case "pointerdown":
				if (CONFIG.inFrame) {
					event.stopPropagation();
					Mapy.Util.openInNewWindow();
				// kliknuti na caru - pridavani noveho bodu a jeho presun; jde pouze pri aktivnim bodu
				} else if (this._line.elTest(event.target) && isMouseLeftOrTouch(event) && !this._owner.readOnly) {
					event.stopPropagation();
					event.preventDefault();

					const coords = this._scene.coordsFromEvent(event);

					// novy bod
					this._newPoint = this._owner.add(coords, this._index);
					this._owner.allowHover = false;
					this._wasEnabled = this._owner.interactivePoint.enabled;
					this._owner.interactivePoint.hide(true);

					document.addEventListener("pointermove", this);
					document.addEventListener("pointerup", this);

					this._owner.dragging = true;
				// modifikace souradnic bodu - nejde pro readonly (this._ownLinePoints)
				} else if (this._point.node === event.target && isMouseLeftOrTouch(event) && !this._owner.readOnly) {
					event.preventDefault();
					event.stopPropagation();

					// posledni - zmena na carkovanou caru, vytvorime popup se vzdalenosti
					if (this._prev && !this._next) {
						this._prev.line.setStyleDash();
						this._owner.interactivePoint.addPopup();
					}

					this._owner.allowHover = false;

					document.addEventListener("pointermove", this);
					document.addEventListener("pointerup", this);
					document.addEventListener("pointercancel", this);

					this._owner.dragging = true;
				}
				break;

			case "pointermove":
				// presun noveho bodu
				if (this._newPoint) {
					const coords = this._scene.coordsFromEvent(event);

					// upravime koncovy bod pred nami
					if (this._newPoint.prev) {
						this._newPoint.prev.endCoords = coords;
					}

					// aktualizace dat
					this._newPoint.startCoords = coords;

					// update - area, distance, posledni radek
					this._owner.redraw({
						noFull: true,
					});
				} else {
					const coords = this._scene.coordsFromEvent(event);

					// tahani bodu - presun cary
					event.preventDefault();
					event.stopPropagation();

					// aktaulizace dat
					this._startC = coords;
					this._point.coords = coords;
					this._point.setNoEvents(true);

					// update popupu
					if (this._owner.interactivePoint.popup && this._prev) {
						const distance = Math.round(this._prev.getCoords().distance(coords));

						this._owner.interactivePoint.setPopup(distance, event.clientX, event.clientY);
					}

					// pokud mame koncovy bod, prepocitame caru
					if (this._endC) {
						this._line.coords = this._getLinePoints();
					}

					// update dat pred nama
					if (this._prev) {
						this._prev.endCoords = coords;
					}

					// update - area, distance, posledni radek
					this._owner.redraw({
						noFull: true,
					});
				}
				break;

			case "pointercancel":
			case "pointerup":
				// pusteni mysi pri novem bodu/presunu
				if (event.currentTarget === document || this._newPoint) {
					this._owner.logPoint(this._newPoint, this.getCoords());
					this._owner.allowHover = true;
					this._owner.sectionHighlight(false, -1, true);
					document.removeEventListener("pointermove", this);
					document.removeEventListener("pointerup", this);
					document.removeEventListener("pointercancel", this);

					// rebind prvku okolo a sama sebe
					if (this._next) {
						this._next.bindTo();
					}

					if (this._prev) {
						this._prev.bindTo();
					}

					this.bindTo();
					this._owner.interactivePoint.removePopup();
					this._owner.redraw();

					if (this._newPoint && this._wasEnabled) {
						this._owner.interactivePoint.show(event);
					}

					this._point.setNoEvents();

					this._owner.setDefCursor();

					this._owner.dragging = false;

					Mapy.getComponent("history").save(true);
				}

				this._newPoint = null;
				this._wasEnabled = false;
				break;

			case "pointerenter":
				if (this._owner.dragging) {
					break;
				}

				// najeti na bod
				if (this._point.node === event.target) {
					this._owner.sectionHighlight(true, Math.max(this._index - 1, 0), true);
				// najeti na caru
				} else if (this._line.elTest(event.target)) {
					this._owner.sectionHighlight(true, this._index, true);

					if (!this._owner.readOnly) {
						this._owner.setAddCursor();
					}
				}
				break;

			case "pointerleave":
				if (this._owner.dragging) {
					break;
				}

				// odjeti z bodu
				if (this._point.node === event.target) {
					this._owner.sectionHighlight(false, Math.max(this._index - 1, 0), true);
				// odjeti z cary
				} else if (this._line.elTest(event.target)) {
					this._owner.sectionHighlight(false, this._index, true);
					this._owner.setDefCursor();
				}
				break;
			default:
				//
		}
	}

	/**
	 * Vytvoreni bodu - sam se pripne do vrstvy.
	 */
	_createPoint() {
		if (!this._startC) {
			return;
		}

		this._point.create(this._type === TYPES.START, this._startC);
		// bind na nas handleEvent
		this._point.bindTo(this);
	}

	/**
	 * Vytvoreni cary - sama se pripne do vrstvy.
	 */
	_createLine() {
		if (!this._endC) {
			return;
		}

		const points = this._getLinePoints();

		this._line.create(points);
		// bind na nas handleEvent
		this._line.bindTo(this);
	}

	/**
	 * Ziskani bodu cary - bud si je spocitame pres zacatek a konec nebo je mame definovane.
	 */
	_getLinePoints() {
		return this._ownLinePoints || Line.calculateLinePoints(this._startC, this._endC);
	}
}

